/**
 * alertWindow — the ONE definition of the Dashboard "Recent Alerts" period.
 *
 * WHY THIS EXISTS
 *
 * `dashboard_alertPeriod` is a localStorage key written by the Dashboard and read by the
 * Charts symbol pane, so the Charts "Alerts" filter can reproduce the SAME list the
 * Dashboard panel is showing. That is a real contract, and it was being honoured by two
 * hand-written parsers that did not agree on what they returned:
 *
 *   DashboardLayout.tsx  `alertPeriodToHours('1d')` -> 1        ... DAYS, despite the name
 *   ChartsPage.tsx       regex /^(\d+)([hd])$/      -> 24       ... hours
 *
 * Both were correct for their own caller and neither was wrong on any of the ten legal
 * values, but nothing enforced that and the names actively misled. A third caller copying
 * either one had even odds of being off by 24x.
 *
 * So: one table, two accessors, each named for what it actually returns.
 */

/**
 * The values the period selector offers, in display order. Exported so the Charts page
 * renders exactly the same list as the Dashboard — two hand-maintained copies of a set of
 * option values is how they drift apart.
 */
export const ALERT_PERIODS = ['1h', '2h', '4h', '6h', '12h', '1d', '3d', '7d', '14d', '30d'] as const;

/** The same values, in hours. */
const PERIOD_HOURS: Record<string, number> = {
  '1h': 1, '2h': 2, '4h': 4, '6h': 6, '12h': 12,
  '1d': 24, '3d': 72, '7d': 168, '14d': 336, '30d': 720,
};

/** Default when the key is absent or holds something unrecognised. */
export const DEFAULT_ALERT_PERIOD = '1d';

/** The window in HOURS. Use for `Date` arithmetic. */
export function alertPeriodToHours(period: string): number {
  const known = PERIOD_HOURS[period];
  if (known != null) return known;
  // Tolerate a shape the table does not list (e.g. '8h', '2d') rather than silently
  // collapsing to the default -- the selector is the only writer today, but the key is
  // plain localStorage and a user can put anything in it.
  const m = /^(\d+)([hd])$/.exec(period || '');
  if (m) return m[2] === 'd' ? parseInt(m[1], 10) * 24 : parseInt(m[1], 10);
  return PERIOD_HOURS[DEFAULT_ALERT_PERIOD];
}

/** The same window in DAYS, which is what `fetchDashboardSummary` takes. Fractional
 *  below 1d — `'6h'` is 0.25, not 0. */
export function alertPeriodToDays(period: string): number {
  return alertPeriodToHours(period) / 24;
}

/** Read the persisted period, with the default applied. */
export function readAlertPeriod(): string {
  try { return localStorage.getItem('dashboard_alertPeriod') || DEFAULT_ALERT_PERIOD; }
  catch { return DEFAULT_ALERT_PERIOD; }
}
